import React, { useState, useEffect, useMemo } from 'react';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, Download, RefreshCw, DollarSign, Activity, TrendingUp, CreditCard } from 'lucide-react';

const BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3007';

// Helper functions for formatting numbers
const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  } else if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}k`;
  } else {
    return tokens.toLocaleString();
  }
};

const formatCurrency = (amount: number): string => {
  return isNaN(amount) ? '0.00' : amount.toFixed(2);
};

const formatCurrencyDetailed = (amount: number): string => {
  return isNaN(amount) ? '0.0000' : amount.toFixed(4);
};

const safeNumber = (value: any): number => {
  return Number(value || 0) || 0;
};

interface UsageActivity {
  id: string;
  userId: string;
  serviceDefinitionId: string;
  serviceType: string;
  provider: string;
  model: string;
  creditsUsed: number;
  estimatedCost: number;
  actualCost?: number;
  theoreticalCredits?: number;
  inputTokens?: number;
  outputTokens?: number;
  imageCount?: number;
  storageBytes?: number;
  conversationId?: string;
  artifactId?: string;
  agentId?: string;
  requestMetadata?: any;
  status: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  service?: {
    serviceType: string;
    providerName: string;
    modelIdentifier: string;
    displayName: string;
  };
}

interface PricingSettings {
  marginPercent: number;
  creditsPerDollar: number;
}

interface UsageSummary {
  serviceType: string;
  requestCount: number;
  totalCredits: number;
  totalCost: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
}

interface DateRange {
  start: Date;
  end: Date;
}

const SERVICE_TYPES = [
  { value: 'all', label: 'All Services' },
  { value: 'llm', label: 'LLM' },
  { value: 'image_generation', label: 'Image Generation' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'storage', label: 'Storage' },
  { value: 'search', label: 'Search' },
  { value: 'knowledge_base', label: 'Knowledge Base' }
];

const QUICK_RANGES = [
  { label: 'Last Hour', getValue: () => ({ start: subDays(new Date(), 1/24), end: new Date() }) },
  { label: 'Today', getValue: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }) },
  { label: 'Yesterday', getValue: () => ({ start: startOfDay(subDays(new Date(), 1)), end: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'Last 7 Days', getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Last 30 Days', getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) }
];

export const CreditTrackingPage: React.FC = () => {
  const { user } = useAuth();

  // State
  const [activities, setActivities] = useState<UsageActivity[]>([]);
  const [summary, setSummary] = useState<UsageSummary[]>([]);
  const [pricingSettings, setPricingSettings] = useState<PricingSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => QUICK_RANGES[1]?.getValue() || { start: startOfDay(new Date()), end: endOfDay(new Date()) });
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'createdAt' | 'creditsUsed' | 'estimatedCost'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('activities');

  // Fetch activities
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : '';
      const params = new URLSearchParams({
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
        limit: '500'
      });
      
      if (serviceTypeFilter !== 'all') {
        params.append('serviceType', serviceTypeFilter);
      }
      
      const response = await fetch(`${BASE_URL}/api/admin/credit-tracking/activities?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform field names from snake_case to camelCase for consistency
        const transformedActivities = (data.activities || []).map((activity: any) => {
          // Remove the snake_case versions to avoid duplicate processing
          const { credits_used, estimated_cost, actual_cost, input_tokens, output_tokens, theoretical_credits, ...cleanActivity } = activity;
          return {
            ...cleanActivity,
            creditsUsed: activity.creditsUsed || credits_used || 0,
            estimatedCost: activity.estimatedCost || estimated_cost || 0,
            actualCost: activity.actualCost || actual_cost || 0,
            theoreticalCredits: activity.theoreticalCredits || theoretical_credits || 0,
            inputTokens: activity.inputTokens || input_tokens || 0,
            outputTokens: activity.outputTokens || output_tokens || 0,
          };
        });
        setActivities(transformedActivities);
        // Store pricing settings if returned
        if (data.pricingSettings) {
          setPricingSettings(data.pricingSettings);
        }
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : '';
      const params = new URLSearchParams({
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString()
      });
      
      const response = await fetch(`${BASE_URL}/api/admin/credit-tracking/summary?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform summary data field names
        const transformedSummary = (data.data || []).map((item: any) => {
          // Remove the snake_case versions to avoid duplicate processing
          const { total_credits, total_cost, total_input_tokens, total_output_tokens, ...cleanItem } = item;
          return {
            ...cleanItem,
            totalCredits: item.totalCredits || total_credits || 0,
            totalCost: item.totalCost || total_cost || 0,
            totalInputTokens: item.totalInputTokens || total_input_tokens || 0,
            totalOutputTokens: item.totalOutputTokens || total_output_tokens || 0,
          };
        });
        setSummary(transformedSummary);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  // Filtered and sorted activities
  const filteredActivities = useMemo(() => {
    let filtered = activities;
    
    // Filter by user
    if (userFilter) {
      filtered = filtered.filter(a => {
        const fullName = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.trim();
        return a.user?.email?.toLowerCase().includes(userFilter.toLowerCase()) ||
          fullName.toLowerCase().includes(userFilter.toLowerCase()) ||
          a.userId.includes(userFilter);
      });
    }
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(a => 
        a.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.conversationId?.includes(searchQuery) ||
        a.artifactId?.includes(searchQuery) ||
        a.agentId?.includes(searchQuery)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal: any = a[sortBy];
      let bVal: any = b[sortBy];
      
      if (sortBy === 'createdAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [activities, userFilter, searchQuery, sortBy, sortOrder]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredActivities.reduce((acc, activity) => ({
      credits: acc.credits + safeNumber(activity.creditsUsed),
      cost: acc.cost + safeNumber(activity.estimatedCost),
      requests: acc.requests + 1,
      inputTokens: acc.inputTokens + safeNumber(activity.inputTokens),
      outputTokens: acc.outputTokens + safeNumber(activity.outputTokens)
    }), { credits: 0, cost: 0, requests: 0, inputTokens: 0, outputTokens: 0 });
  }, [filteredActivities]);

  // Load data on mount and when filters change
  useEffect(() => {
    // Only fetch data if we have an authenticated user who is an admin
    if (auth.currentUser && user?.isAdmin) {
      fetchActivities();
      fetchSummary();
    }
  }, [dateRange, serviceTypeFilter, refreshKey, user]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Service', 'Provider', 'Model', 'Credits', 'Cost', 'Input Tokens', 'Output Tokens', 'Status'];
    const rows = filteredActivities.map(a => [
      format(new Date(a.createdAt), 'yyyy-MM-dd HH:mm:ss'),
      a.user?.email || a.userId,
      a.serviceType,
      a.provider,
      a.model,
      safeNumber(a.creditsUsed),
      formatCurrencyDetailed(safeNumber(a.estimatedCost)),
      safeNumber(a.inputTokens) || '',
      safeNumber(a.outputTokens) || '',
      a.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credit-usage-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credit Tracking</h1>
          <p className="text-muted-foreground">Monitor and analyze credit usage across the platform</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={filteredActivities.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.requests.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              In selected period
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.credits.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Total consumption
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estimated Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${formatCurrency(totals.cost)}
            </div>
            <p className="text-xs text-muted-foreground">
              Platform cost
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTokens(totals.inputTokens + totals.outputTokens)}
            </div>
            <p className="text-xs text-muted-foreground">
              Input + Output
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Range */}
          <div className="flex gap-2 flex-wrap">
            {QUICK_RANGES.map(range => (
              <Button
                key={range.label}
                variant={dateRange.start.getTime() === range.getValue().start.getTime() ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(range.getValue())}
              >
                {range.label}
              </Button>
            ))}
          </div>
          
          {/* Other Filters */}
          <div className="grid gap-4 md:grid-cols-4">
            <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Service type" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              placeholder="Filter by user..."
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            />
            
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            <Select 
              value={`${sortBy}-${sortOrder}`} 
              onValueChange={(value) => {
                const [field, order] = value.split('-');
                setSortBy(field as any);
                setSortOrder(order as any);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt-desc">Newest First</SelectItem>
                <SelectItem value="createdAt-asc">Oldest First</SelectItem>
                <SelectItem value="creditsUsed-desc">Most Credits</SelectItem>
                <SelectItem value="estimatedCost-desc">Highest Cost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="activities">Activity Log</TabsTrigger>
          <TabsTrigger value="summary">Usage Summary</TabsTrigger>
        </TabsList>
        
        <TabsContent value="activities" className="space-y-4">
          {/* Activity Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Activity Log</CardTitle>
                  <CardDescription>
                    {filteredActivities.length} entries found
                  </CardDescription>
                </div>
                {pricingSettings && (
                  <div className="text-right text-sm text-muted-foreground">
                    <div>Current Pricing Settings:</div>
                    <div className="font-mono">
                      {pricingSettings.marginPercent.toFixed(2)}% margin, {pricingSettings.creditsPerDollar.toLocaleString()} credits/$
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Actual Cost</TableHead>
                      <TableHead className="text-right">
                        <span title="Credits deducted at time of request">Credits Used</span>
                      </TableHead>
                      <TableHead className="text-right">
                        <span title={pricingSettings ? `Credits if using current settings (${pricingSettings.marginPercent.toFixed(2)}% margin, ${pricingSettings.creditsPerDollar} credits/$)` : 'Credits with current pricing'}>
                          Current Pricing
                        </span>
                      </TableHead>
                      <TableHead className="text-right">Tokens</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredActivities.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground">
                          No activities found for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredActivities.map((activity) => (
                        <React.Fragment key={activity.id}>
                          <TableRow>
                            <TableCell className="font-mono text-sm">
                              {format(new Date(activity.createdAt), 'HH:mm:ss')}
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate" title={activity.user?.email}>
                              {activity.user?.email || activity.userId.slice(0, 8)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {activity.serviceType}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate" title={activity.model}>
                              {activity.model}
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {(() => {
                                const actualCost = safeNumber(activity.actualCost);
                                const calculatedCost = safeNumber((activity as any).calculatedBaseCost);

                                // If we have a calculated cost from service definition, show it with indicator
                                if (actualCost === 0 && calculatedCost > 0) {
                                  return (
                                    <span title="Calculated from service definition pricing (API did not provide cost)">
                                      ~${calculatedCost < 0.0001 ? calculatedCost.toExponential(2) : formatCurrencyDetailed(calculatedCost)}
                                    </span>
                                  );
                                }

                                // Show actual cost, using scientific notation for very small values
                                if (actualCost > 0 && actualCost < 0.0001) {
                                  return `$${actualCost.toExponential(2)}`;
                                }

                                return `$${formatCurrencyDetailed(actualCost)}`;
                              })()}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {safeNumber(activity.creditsUsed).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {(() => {
                                const theoretical = safeNumber(activity.theoreticalCredits);
                                const actual = safeNumber(activity.creditsUsed);
                                const diff = theoretical - actual;
                                const diffPercent = actual > 0 ? ((diff / actual) * 100) : 0;

                                if (theoretical === 0 && actual === 0) return '-';

                                return (
                                  <span
                                    className={cn(
                                      Math.abs(diffPercent) > 5 && diff > 0 && "text-red-500",
                                      Math.abs(diffPercent) > 5 && diff < 0 && "text-green-500"
                                    )}
                                    title={`Difference: ${diff >= 0 ? '+' : ''}${diff.toFixed(1)} (${diffPercent >= 0 ? '+' : ''}${diffPercent.toFixed(1)}%)`}
                                  >
                                    {theoretical.toLocaleString()}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {(() => {
                                const inputTokens = safeNumber(activity.inputTokens);
                                const outputTokens = safeNumber(activity.outputTokens);
                                const totalTokens = inputTokens + outputTokens;
                                
                                if (totalTokens === 0) return '-';
                                
                                return (
                                  <span title={`In: ${inputTokens.toLocaleString()}, Out: ${outputTokens.toLocaleString()}`}>
                                    {formatTokens(totalTokens)}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={activity.status === 'completed' ? 'default' : 'destructive'}>
                                {activity.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedRows(prev => {
                                  const next = new Set(prev);
                                  if (next.has(activity.id)) {
                                    next.delete(activity.id);
                                  } else {
                                    next.add(activity.id);
                                  }
                                  return next;
                                })}
                              >
                                {expandedRows.has(activity.id) ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                          {expandedRows.has(activity.id) && (
                            <TableRow>
                              <TableCell colSpan={10} className="bg-muted/50">
                                <div className="p-4 space-y-2 text-sm">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {activity.conversationId && (
                                      <div>
                                        <span className="font-medium">Conversation:</span>
                                        <br />
                                        <span className="font-mono text-xs">{activity.conversationId}</span>
                                      </div>
                                    )}
                                    {activity.artifactId && (
                                      <div>
                                        <span className="font-medium">Artifact:</span>
                                        <br />
                                        <span className="font-mono text-xs">{activity.artifactId}</span>
                                      </div>
                                    )}
                                    {activity.agentId && (
                                      <div>
                                        <span className="font-medium">Agent:</span>
                                        <br />
                                        <span className="font-mono text-xs">{activity.agentId}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="font-medium">Provider:</span>
                                      <br />
                                      <span className="font-mono text-xs">{activity.provider}</span>
                                    </div>
                                  </div>
                                  {activity.requestMetadata && (
                                    <div>
                                      <span className="font-medium">Metadata:</span>
                                      <pre className="mt-2 p-2 bg-background rounded text-xs overflow-x-auto">
                                        {JSON.stringify(activity.requestMetadata, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="summary" className="space-y-4">
          {/* Service Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Usage by Service Type</CardTitle>
              <CardDescription>
                Aggregated usage statistics for the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Type</TableHead>
                      <TableHead className="text-right">Requests</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          No data available for the selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      summary.map((s: UsageSummary) => (
                        <TableRow key={s.serviceType}>
                          <TableCell>
                            <Badge variant="outline">{s.serviceType}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {s.requestCount}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {safeNumber(s.totalCredits).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            ${formatCurrency(safeNumber(s.totalCost))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CreditTrackingPage;